import { useEffect, useRef } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { getDeviceHints } from "@/lib/deviceHints";
import { useTheme } from "@/context/ThemeContext";
import { takeLangMode } from "@/lib/i18n";

const PRESENCE_TICK_MS = 15000;    // «نشاط الزوار»: one presence beacon per 15s per tab, only when visible + (interacted ≤60s OR video playing)
const PRESENCE_WINDOW_MS = 60000;

const ENGAGE_TICK_MS = 15000;      // one beacon per 15s per tab, at most
const INTERACTION_WINDOW_MS = 30000; // reader counts as engaged if they interacted within the last 30s

const tabId = (() => {
    try {
        let id = sessionStorage.getItem("apv_tab");
        if (!id) { id = Math.random().toString(36).slice(2, 12); sessionStorage.setItem("apv_tab", id); }
        return id;
    } catch { return "na"; }
})();

const utmFromSearch = (search) => {
    const q = new URLSearchParams(search || "");
    const out = {};
    for (const k of ["source", "medium", "campaign"]) { const v = q.get(`utm_${k}`); if (v) out[k] = v.slice(0, 80); }
    return Object.keys(out).length ? out : null;
};

const beacon = (url, body) => {
    try {
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    } catch (_) { /* fall through */ }
    fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
};

// Anonymous, first-party visitor analytics tracker.
//
// - Mounted inside PublicLayout only (never on /admin, /login etc)
// - Fires ONE pageview per real navigation via navigator.sendBeacon
// - Dedupes React-strict-mode / re-render doubles via lastKey guard
// - Sends a light heartbeat every 60s so the visitor counts as "online now"
// - Sends an engagement tick every 15s ONLY while the tab is visible and the reader interacted recently
export default function AnalyticsTracker() {
    const location = useLocation();
    const arMatch = useMatch("/article/:slug");
    const enMatch = useMatch("/en/article/:slug"), esMatch = useMatch("/es/article/:slug"), nlMatch = useMatch("/nl/article/:slug");
    const articleMatch = arMatch || enMatch || esMatch || nlMatch;
    const lastKeyRef = useRef("");
    const heartbeatRef = useRef(null);
    const landingRef = useRef(true);      // true only for the first pageview of this page load
    const lastInteractionRef = useRef(Date.now());
    const utmRef = useRef(utmFromSearch(window.location.search));
    const { theme, source: themeSource } = useTheme();
    const themeRef = useRef({ mode: theme, source: themeSource });
    themeRef.current = { mode: theme, source: themeSource };

    // Manual day/night toggle → update the current session only (no pageview, no new visit). Dedupes per mode.
    useEffect(() => {
        let lastSent = "";
        const onChange = (e) => {
            const mode = e?.detail?.mode;
            if (!mode || mode === lastSent) return;
            lastSent = mode;
            beacon(`${API_BASE}/public/analytics/theme`, JSON.stringify({ mode, tab_id: tabId }));
        };
        window.addEventListener("theme:manual-change", onChange);
        return () => window.removeEventListener("theme:manual-change", onChange);
    }, []);

    // Fire pageview whenever the pathname changes (query changes ignored)
    useEffect(() => {
        const key = location.pathname; // dedupe by path only
        if (lastKeyRef.current === key) return;
        lastKeyRef.current = key;
        let cancelled = false;
        (async () => {
        const hints = await getDeviceHints();   // adds device model where the browser exposes it; never blocks the visit
        if (cancelled) return;
        const landing = landingRef.current;
        landingRef.current = false;
        const body = JSON.stringify({
            path: location.pathname,
            referrer: document.referrer || "",
            article_slug: articleMatch?.params?.slug || null,
            hints,
            utm: landing ? utmRef.current : null,
            lang: /^\/(en|es|nl)(\/|$)/.exec(location.pathname)?.[1] || "ar",
            landing,
            tab_id: tabId,
            theme: themeRef.current,
            lang_mode: takeLangMode(location.pathname),
            title: (document.title || "").slice(0, 300),
        });
        beacon(`${API_BASE}/public/analytics/pageview`, body);
        })();
        return () => { cancelled = true; if (lastKeyRef.current === key) lastKeyRef.current = ""; };   // cancelled before send → allow the re-run to send once
    }, [location.pathname, articleMatch?.params?.slug]);

    // Heartbeat every 60s while the tab is visible
    useEffect(() => {
        const sendHeartbeat = () => {
            if (document.visibilityState !== "visible") return;
            beacon(`${API_BASE}/public/analytics/heartbeat`, JSON.stringify({ path: window.location.pathname }));
        };
        // First heartbeat after 15s so the visitor shows as "online now" quickly
        const first = setTimeout(sendHeartbeat, 15000);
        heartbeatRef.current = setInterval(sendHeartbeat, 60000);
        const onVis = () => { if (document.visibilityState === "visible") sendHeartbeat(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearTimeout(first);
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    // Engagement ticks: listeners only stamp a timestamp (no network); the interval decides whether to send.
    useEffect(() => {
        const mark = () => { lastInteractionRef.current = Date.now(); };
        const opts = { passive: true, capture: true };
        const evs = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"];
        evs.forEach((e) => window.addEventListener(e, mark, opts));
        let lastTick = Date.now();
        const tick = () => {
            const now = Date.now();
            const visible = document.visibilityState === "visible";
            const recent = now - lastInteractionRef.current < INTERACTION_WINDOW_MS;
            if (visible && recent) {
                beacon(`${API_BASE}/public/analytics/engage`, JSON.stringify({ path: window.location.pathname, tab_id: tabId, engaged_ms: Math.min(now - lastTick, ENGAGE_TICK_MS) }));
            }
            lastTick = now;
        };
        const iv = setInterval(tick, ENGAGE_TICK_MS);
        const onHide = () => { if (document.visibilityState === "hidden") { tick(); } };
        document.addEventListener("visibilitychange", onHide);
        return () => {
            clearInterval(iv);
            evs.forEach((e) => window.removeEventListener(e, mark, opts));
            document.removeEventListener("visibilitychange", onHide);
        };
    }, []);

    // Presence (live activity): video playback is detected from real <video> play/pause/ended events.
    useEffect(() => {
        const playing = new Set();
        const onPlay = (e) => { if (e.target?.tagName === "VIDEO") playing.add(e.target); };
        const onStop = (e) => { if (e.target?.tagName === "VIDEO") playing.delete(e.target); };
        document.addEventListener("playing", onPlay, true);
        ["pause", "ended", "emptied", "waiting"].forEach((ev) => document.addEventListener(ev, onStop, true));
        const tick = () => {
            if (document.visibilityState !== "visible") return;
            for (const v of [...playing]) if (!v.isConnected || v.paused || v.ended) playing.delete(v);
            const vid = [...playing].find((v) => !v.paused && !v.ended && v.readyState >= 2);
            const recent = Date.now() - lastInteractionRef.current < PRESENCE_WINDOW_MS;
            if (!vid && !recent) return;
            const slug = (/^\/(?:en\/|es\/|nl\/)?(?:article|video)\/([^/?#]+)/.exec(window.location.pathname) || [])[1] || null;
            beacon(`${API_BASE}/public/analytics/presence`, JSON.stringify({
                path: window.location.pathname, slug: slug ? decodeURIComponent(slug) : null,
                lang: /^\/(en|es|nl)(\/|$)/.exec(window.location.pathname)?.[1] || "ar",
                kind: vid ? "video" : "page", video_id: vid ? (vid.dataset.articleId || vid.dataset.reelId || null) : null, tab_id: tabId,
            }));
        };
        const first = setTimeout(tick, 3000);
        const iv = setInterval(tick, PRESENCE_TICK_MS);
        return () => {
            clearTimeout(first); clearInterval(iv);
            document.removeEventListener("playing", onPlay, true);
            ["pause", "ended", "emptied", "waiting"].forEach((ev) => document.removeEventListener(ev, onStop, true));
        };
    }, []);

    return null;
}
